import { Mail, Phone, Clock, MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead } from "../components/Motion";
import { BeforeAfter } from "../components/BeforeAfter";
import { ContactForm } from "../components/Forms";
import { ClientStrip, FaqBlock, FinalCta, FounderPortrait, HowItWorks, ReviewsWall, ServiceArea, WarrantyModule } from "../components/Sections";
import { BUSINESS, FAQS, ILIAS_BIO, PROJECTS, img } from "../data/site";

const DUST_FREE = [
  { t: "Sealed work zones", d: "Plastic sheeting and zip walls close off the area we're working in, so dust and overspray stay put." },
  { t: "Protected floors & furniture", d: "Floors masked, furniture moved and covered before any prep begins." },
  { t: "Dust captured at the source", d: "Vacuum-attached sanders pull dust as it's created instead of letting it settle." },
  { t: "Daily clean-down", d: "Every working day ends with a vacuum and wipe-down, so your space is usable each evening." },
  { t: "Final clean & walkthrough", d: "Protection removed, furniture returned, and a walkthrough together before we leave." },
];

export function ProcessPage() {
  return (
    <>
      <Seo title="Our Process | The Amora Dust-Free Promise™ | Toronto Painters" description="How Amora Painting works: a free Colour & Scope Visit, a written quote within 24 hours, dust-free execution and a final walkthrough with warranty." />
      <PageHero eyebrow="Our process" lines={["Four steps.", "Zero surprises."]} intro="From the first visit to the final walkthrough, you always know what's happening, when it's happening and what it includes." image={img.roller2} alt="Painter rolling a clean coat of paint onto a wall" />
      <HowItWorks dark={false} />
      <section className="bg-navy py-20 sm:py-28" data-testid="dust-free-breakdown">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5"><SectionHead dark eyebrow="The Amora Dust-Free Promise™" title="What dust-free actually means." intro="It's not a slogan. It's five things our crews do on every single job." /></div>
          <ol className="lg:col-span-7">
            {DUST_FREE.map((s, i) => (
              <Reveal as="li" key={s.t} className="flex gap-6 border-t border-white/10 py-8 first:border-t-0 first:pt-0">
                <span className="font-display text-3xl font-extrabold text-flame">0{i + 1}</span>
                <div><h3 className="text-xl font-bold text-white">{s.t}</h3><p className="mt-2 text-slate-300">{s.d}</p></div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>
      <WarrantyModule />
      <FaqBlock faqs={FAQS} />
      <FinalCta />
    </>
  );
}

const VALUES = [
  { t: "Dust-free, always", d: "The flagship. Your home or business stays clean and usable while we work." },
  { t: "On the date promised", d: "Start and finish dates in writing — and a schedule built to keep them." },
  { t: "Obsessive about detail", d: "Clean cut lines, proper prep, no missed edges. The things you notice later." },
  { t: "Outcomes you'll show off", d: "Spaces that look finished, not just painted." },
];

export function AboutPage() {
  return (
    <>
      <Seo title="About Amora Painting | 30 Years Painting Toronto, the GTA & Ontario" description="Meet Ilias Lagogiannis, founder and lead estimator of Amora Painting. 30 years of dust-free residential and commercial painting across Toronto and Ontario." />
      <PageHero eyebrow="About" lines={["30 years.", "One standard."]} intro="Amora Painting is a Toronto painting company built around a simple idea: treat every home like it's your own, and every commercial space like business depends on it." cta={false} />
      <section className="bg-paper py-20 sm:py-28" data-testid="about-founder">
        <div className="container-x grid items-start gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5"><FounderPortrait className="aspect-[4/5]" /></Reveal>
          <div className="lg:col-span-7">
            <p className="eyebrow text-flame-600">Founder & Lead Estimator</p>
            <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">{BUSINESS.founder}</h2>
            <Reveal><p className="mt-8 text-lg leading-relaxed text-slate-700" data-testid="about-ilias-bio">{ILIAS_BIO}</p></Reveal>
            <p className="mt-8 border-l-4 border-flame pl-5 font-display text-2xl font-bold text-navy">Ilias personally scopes every project.</p>
          </div>
        </div>
      </section>
      <section className="bg-white py-20 sm:py-28" data-testid="about-values">
        <div className="container-x">
          <SectionHead eyebrow="What we're known for" title="The Amora standard." />
          <div className="mt-12 grid gap-px overflow-hidden rounded-md bg-slate-200 md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => <Reveal key={v.t} delay={i * 0.06} className="bg-white p-8"><h3 className="text-xl font-bold text-navy">{v.t}</h3><p className="mt-3 text-sm text-slate-600">{v.d}</p></Reveal>)}
          </div>
        </div>
      </section>
      <ClientStrip />
      <ServiceArea />
      <FinalCta />
    </>
  );
}

export function ProjectsPage() {
  return (
    <>
      <Seo title="Painting Projects | Before & After | Amora Painting Toronto" description="Before and after painting projects from across Toronto, the GTA and Ontario — interiors, exteriors, cabinets and commercial spaces by Amora Painting." />
      <PageHero eyebrow="Projects" lines={["Before.", "After."]} intro="Drag any slider to compare. Every project is captioned by neighbourhood, scope and duration." cta={false} />
      <section className="bg-white py-20 sm:py-28" data-testid="projects-grid">
        <div className="container-x grid gap-x-10 gap-y-16 md:grid-cols-2">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 0.08}>
              <BeforeAfter {...p} testId={`project-slider-${p.id}`} />
              <div className="mt-6 grid gap-5 rounded-md border border-slate-200 bg-paper p-6 text-sm sm:grid-cols-3" data-testid={`case-study-${p.id}`}>
                {[["The brief", p.caseStudy?.brief], ["What we did", p.caseStudy?.did], ["The result", p.caseStudy?.result]].map(([h, t]) => (
                  <div key={h}><p className="font-mono text-[11px] uppercase tracking-widest text-flame-600">{h}</p><p className="mt-2 text-slate-600">{t}</p></div>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <FinalCta />
    </>
  );
}

export function ReviewsPage() {
  return (
    <>
      <Seo title="Amora Painting Reviews | Toronto & GTA Painting Company" description="Read reviews from Amora Painting clients across Toronto, the GTA and Ontario, and leave your own on Google." />
      <PageHero eyebrow="Reviews" lines={["In their", "own words."]} intro="We only publish real reviews from real clients — never edited, never invented. Here's the standard we hold ourselves to on every job." cta={false} />
      <ReviewsWall />
      <FinalCta />
    </>
  );
}

export function ContactPage() {
  const rows = [
    [Phone, "Call", BUSINESS.phone, BUSINESS.phoneHref, "contact-phone-link"],
    [Mail, "Email", BUSINESS.email, `mailto:${BUSINESS.email}`, "contact-email-link"],
    [Clock, "Hours", BUSINESS.hours, null, "contact-hours"],
    [MapPin, "Service area", "All of the GTA, cottage country, and commercial across Ontario", null, "contact-area"],
  ];
  return (
    <>
      <Seo title="Contact Amora Painting | (647) 985-8007 | Toronto Painters" description="Contact Amora Painting. Call (647) 985-8007 or email amorapaint@gmail.com to book a free Colour & Scope Visit anywhere in the GTA." />
      <PageHero eyebrow="Contact" lines={["Let's talk", "about your space."]} intro="Call, email or send a message. The fastest way to a written quote is a free Colour & Scope Visit." />
      <section className="bg-paper py-20 sm:py-28">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <ul className="space-y-8 lg:col-span-5">
            {rows.map(([Icon, label, value, href, id]) => (
              <li key={label} className="flex gap-4" data-testid={id}>
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-navy text-flame"><Icon className="h-5 w-5" /></span>
                <div><p className="eyebrow text-slate-600">{label}</p>{href ? <a href={href} className="mt-1 block font-display text-xl font-bold text-navy hover:text-flame-600">{value}</a> : <p className="mt-1 font-display text-xl font-bold text-navy">{value}</p>}</div>
              </li>
            ))}
          </ul>
          <Reveal className="lg:col-span-7"><ContactForm /></Reveal>
        </div>
      </section>
    </>
  );
}
